/**
 * src/components/photos/TaskPhotoStrip.jsx — TICKET-765 (CEO 2026-09-04)
 *
 * Extra images on ONE work item. A mark is a task hanging off its anchor photo
 * (tasks.photo_document_id + region); documenting the same defect from a second
 * angle used to force a SECOND task. This strip attaches more photos of the
 * SAME job to that one task, so the item reads as one thing with several views.
 *
 * The anchor photo is NOT listed here — it is the image the user is already
 * looking at, drawn with the mark, directly above this strip.
 *
 * Picker reads the same documents.list the Files tab uses, scoped to this
 * photo's deal/project — no new handler, no cross-feature import of the quote
 * builder's picker. Thumbs render from the backfilled thumb_path (free); a row
 * without one shows its filename instead of a dead <img>.
 *
 * Hooks at top of body. Tokens only. 44px targets. data-testid throughout.
 */

import { useCallback, useEffect, useState } from 'react';
import { ImagePlus, X, Loader } from 'lucide-react';
import { listDealPhotos, thumbUrlFromDoc } from '../../lib/api/documents';
import RegionCrop from './RegionCrop';
import { fetchTaskPhotos, addTaskPhoto, removeTaskPhoto } from '../../lib/api/taskPhotos';

const tileStyle = {
  position: 'relative', width: 84, height: 84, borderRadius: 10, overflow: 'hidden',
  border: '1px solid var(--bdr)', background: 'var(--glass)', flex: 'none',
};

export default function TaskPhotoStrip({ task, photo, showAnchor = false, t = (k, o) => o?.defaultValue || k }) {
  const [extras, setExtras] = useState([]);
  const [picking, setPicking] = useState(false);
  const [candidates, setCandidates] = useState(null); // null = not loaded yet
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const taskId = task?.id || null;
  const anchorId = task?.photo_document_id || null;
  const dealId = photo?.deal_id || null;
  const projectId = photo?.project_id || null;

  useEffect(() => {
    let alive = true;
    if (!taskId) { setExtras([]); return undefined; }
    fetchTaskPhotos(taskId).then((rows) => { if (alive) setExtras(rows); });
    return () => { alive = false; };
  }, [taskId]);

  // The job's photos are also what the attached tiles render their thumbs from,
  // so they load with the strip, not only when the picker opens.
  useEffect(() => {
    let alive = true;
    if (!dealId && !projectId) { setCandidates([]); return undefined; }
    listDealPhotos({ deal_id: dealId, project_id: projectId })
      .then((rows) => { if (alive) setCandidates(rows || []); })
      .catch(() => { if (alive) setCandidates([]); });
    return () => { alive = false; };
  }, [dealId, projectId]);

  // Close the picker when focus moves to another mark — a picker left open over
  // a different task is how the wrong photo gets attached.
  useEffect(() => { setPicking(false); setError(null); }, [taskId]);

  const attach = useCallback(async (documentId) => {
    if (!taskId || busy) return;
    setBusy(true);
    setError(null);
    const res = await addTaskPhoto({ task_id: taskId, photo_document_id: documentId });
    if (res?.success) {
      const rows = await fetchTaskPhotos(taskId);
      setExtras(rows);
      setPicking(false);
    } else {
      setError(res?.error || t('photos.taskPhotos.attachError', { defaultValue: 'Could not attach that photo.' }));
    }
    setBusy(false);
  }, [taskId, busy, t]);

  const detach = useCallback(async (id) => {
    if (busy) return;
    setBusy(true);
    const res = await removeTaskPhoto(id);
    if (res?.success) setExtras((prev) => prev.filter((r) => r.id !== id));
    else setError(res?.error || t('photos.taskPhotos.detachError', { defaultValue: 'Could not remove that photo.' }));
    setBusy(false);
  }, [busy, t]);

  if (!taskId) return null;

  const attachedIds = new Set([anchorId, ...extras.map((r) => r.photo_document_id)].filter(Boolean));
  const pickable = (candidates || []).filter((c) => !attachedIds.has(c.id));

  // TICKET-782 (CEO 2026-09-05): outside the lightbox — a photo work item opened
  // from Projects/Gantt — the anchor image is NOT on screen, so the ticket read as
  // text plus a bundle chip with no picture. Cropped to the mark's region so the
  // ticket shows the defect, not the whole deck.
  const anchorDoc = showAnchor && anchorId ? (candidates || []).find((c) => c.id === anchorId) : null;
  const anchorUrl = anchorDoc ? thumbUrlFromDoc(anchorDoc) : null;

  return (
    <div data-testid="task-photo-strip" style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {showAnchor && anchorId && (
        <div data-testid="task-anchor-photo" style={{ width: '100%' }}>
          {anchorUrl ? (
            <RegionCrop imageUrl={anchorUrl} region={task?.region || null} maxHeight={220} alt={task?.task_name || ''} />
          ) : (
            <div data-testid="task-anchor-photo-missing" style={{
              padding: 12, borderRadius: 10, border: '1px dashed var(--bdr)', background: 'var(--glass)',
              fontSize: 'var(--fs-12)', color: 'var(--tm)', textAlign: 'center',
            }}>
              {t('photos.taskPhotos.anchorUnavailable', { defaultValue: 'Photo preview unavailable' })}
            </div>
          )}
        </div>
      )}
      <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--tm)', fontWeight: 700 }}>
        {t('photos.taskPhotos.title', { defaultValue: 'More photos' })}
        {extras.length > 0 ? ` · ${extras.length}` : ''}
      </span>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {extras.map((r) => (
          <div key={r.id} data-testid="task-photo-tile" style={tileStyle}>
            <TaskPhotoThumb docId={r.photo_document_id} candidates={candidates} />
            <button
              type="button"
              data-testid={`task-photo-remove-${r.id}`}
              aria-label={t('photos.taskPhotos.remove', { defaultValue: 'Remove photo' })}
              onClick={() => detach(r.id)}
              style={{
                position: 'absolute', top: 2, right: 2, width: 26, height: 26, borderRadius: 8,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                background: 'rgba(10,22,40,0.65)', color: 'var(--card)',
              }}
            >
              <X size={14} />
            </button>
          </div>
        ))}

        <button
          type="button"
          data-testid="task-photo-add"
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          style={{
            ...tileStyle, display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 4, minHeight: 44, cursor: 'pointer',
            borderStyle: 'dashed', color: 'var(--t2)', background: 'var(--card)',
            fontSize: 'var(--fs-11)', fontWeight: 600,
          }}
        >
          <ImagePlus size={18} />
          {t('photos.taskPhotos.add', { defaultValue: 'Add photo' })}
        </button>
      </div>

      {error && (
        <div data-testid="task-photo-error" style={{ fontSize: 'var(--fs-12)', color: 'var(--bad)' }}>{error}</div>
      )}

      {picking && (
        <div data-testid="task-photo-picker" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 8, borderRadius: 10, border: '1px solid var(--bdr)', background: 'var(--glass)' }}>
          {candidates === null && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-12)', color: 'var(--tm)' }}>
              <Loader size={14} /> {t('common.loading', { defaultValue: 'Loading…' })}
            </span>
          )}
          {candidates !== null && pickable.length === 0 && (
            <span data-testid="task-photo-picker-empty" style={{ fontSize: 'var(--fs-12)', color: 'var(--tm)' }}>
              {t('photos.taskPhotos.noneLeft', { defaultValue: 'No other photos filed on this job.' })}
            </span>
          )}
          {pickable.map((c) => {
            const url = thumbUrlFromDoc(c);
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`task-photo-pick-${c.id}`}
                onClick={() => attach(c.id)}
                disabled={busy}
                title={c.file_name || ''}
                style={{ ...tileStyle, padding: 0, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
              >
                {url
                  ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 'var(--fs-10)', color: 'var(--t2)', padding: 4, display: 'block', overflowWrap: 'anywhere' }}>{c.file_name || c.id}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Thumb for an already-attached extra: resolved from the job's photo list that
 * the strip already loaded, so a tile costs no extra round trip. A photo with
 * no backfilled thumb shows a neutral placeholder rather than a dead <img>.
 */
function TaskPhotoThumb({ docId, candidates }) {
  const doc = (candidates || []).find((c) => c.id === docId);
  const url = doc ? thumbUrlFromDoc(doc) : null;
  if (!url) {
    return <span style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-10)', color: 'var(--tm)' }}>IMG</span>;
  }
  return <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
}
